import React, { useState, useMemo, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  IconButton,
  Popover,
  Slider,
  Chip,
  Button,
  Tooltip,
  useTheme,
  Fade,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import VerticalAlignTopIcon from "@mui/icons-material/VerticalAlignTop";
import CenterFocusStrongIcon from "@mui/icons-material/CenterFocusStrong";
import CloseFullscreenIcon from "@mui/icons-material/CloseFullscreen";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import {
  getCapsuleDockBottom,
  getCapsuleMaxY,
  shouldHideProgressCapsule,
} from "./capsuleLayout";

const CAPSULE_POS_KEY = "bible_capsule_position";

const getInitialPosition = (isZenMode = false) => {
  if (typeof window === "undefined") return null;
  try {
    const saved = sessionStorage.getItem(CAPSULE_POS_KEY);
    if (!saved) return null;
    const parsed = JSON.parse(saved);
    if (typeof parsed?.x === "number" && typeof parsed?.y === "number") {
      const margin = 8;
      const maxX = Math.max(margin, window.innerWidth - 60 - margin);
      const maxY = getCapsuleMaxY({
        viewportHeight: window.innerHeight,
        capsuleHeight: 44,
        margin,
        isZenMode,
        viewportWidth: window.innerWidth,
      });
      return {
        x: Math.min(Math.max(margin, parsed.x), maxX),
        y: Math.min(Math.max(margin, parsed.y), maxY),
      };
    }
  } catch (e) {
    // Ignore storage parse error
  }
  return null;
};

// Mini SVG Progress Ring
const ProgressRing = ({ percent, size = 26, stroke = 2.8, isCompleted }) => {
  const theme = useTheme();
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (Math.min(100, Math.max(0, percent)) / 100) * circumference;

  const activeColor = isCompleted
    ? theme.palette.mode === "dark"
      ? "#e2c48a"
      : "#8b6914"
    : theme.palette.primary.main;

  return (
    <Box
      sx={{
        position: "relative",
        width: size,
        height: size,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
        {/* Track */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={
            theme.palette.mode === "dark"
              ? "rgba(255, 255, 255, 0.15)"
              : "rgba(0, 0, 0, 0.1)"
          }
          strokeWidth={stroke}
        />
        {/* Indicator */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={activeColor}
          strokeWidth={stroke}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          style={{ transition: "stroke-dashoffset 200ms ease" }}
        />
      </svg>
      <Typography
        variant="caption"
        sx={{
          position: "absolute",
          fontSize: "0.58rem",
          fontWeight: 700,
          color: activeColor,
          userSelect: "none",
          lineHeight: 1,
        }}
      >
        {isCompleted ? "✓" : `${percent}%`}
      </Typography>
    </Box>
  );
};

const ChapterProgressCapsule = ({
  chapterTitle = "",
  currentVerse = 1,
  totalVerses = 1,
  firstVerse = 1,
  lastVerse = 1,
  scrollPercent = 0,
  isScrolling = false,
  scrollDirection = null,
  isCompleted = false,
  progressStyle = "full", // 'full' | 'minimal' | 'top_only' | 'hidden'
  estimatedMinutesRemaining = 0,
  currentSection = null,
  sectionList = [],
  verseTextMap = {},
  hasSelection = false,
  isZenMode = false,
  toggleCompleted,
  scrollToVerse,
  scrollToTop,
  setStyle,
  labels = {},
}) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);
  const [scrubberVerse, setScrubberVerse] = useState(currentVerse);
  const [isHovered, setIsHovered] = useState(false);
  const [justCompletedAnim, setJustCompletedAnim] = useState(false);

  // Draggable position state & interaction refs
  const [position, setPosition] = useState(() => getInitialPosition(isZenMode));
  const [isDragging, setIsDragging] = useState(false);
  const capsuleRef = useRef(null);
  const dragStartRef = useRef({
    startX: 0,
    startY: 0,
    pointerX: null,
    pointerY: null,
  });
  const hasMovedRef = useRef(false);

  // Persist dragged position during the session
  useEffect(() => {
    if (position) {
      try {
        sessionStorage.setItem(CAPSULE_POS_KEY, JSON.stringify(position));
      } catch (e) {
        // Ignore storage error
      }
    }
  }, [position]);

  // Clamp capsule within viewport boundaries on resize or style change
  useEffect(() => {
    if (!position) return;

    const clampToViewport = () => {
      const capsuleEl = capsuleRef.current;
      const width = capsuleEl ? capsuleEl.offsetWidth : 44;
      const height = capsuleEl ? capsuleEl.offsetHeight : 44;
      const margin = 8;
      const maxX = Math.max(margin, window.innerWidth - width - margin);
      const maxY = getCapsuleMaxY({
        viewportHeight: window.innerHeight,
        capsuleHeight: height,
        margin,
        isZenMode,
        viewportWidth: window.innerWidth,
      });

      setPosition((prev) => {
        if (!prev) return null;
        const nextX = Math.min(Math.max(margin, prev.x), maxX);
        const nextY = Math.min(Math.max(margin, prev.y), maxY);
        if (nextX === prev.x && nextY === prev.y) return prev;
        return { x: nextX, y: nextY };
      });
    };

    const timer = setTimeout(clampToViewport, 50);
    window.addEventListener("resize", clampToViewport);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("resize", clampToViewport);
    };
  }, [position, progressStyle, isZenMode]);

  // Pointer drag event handlers
  const handlePointerDown = (event) => {
    if (event.button !== 0 && event.button !== undefined) return;
    if (isPopoverOpen) return;

    const capsuleEl = capsuleRef.current;
    if (!capsuleEl) return;

    const rect = capsuleEl.getBoundingClientRect();
    dragStartRef.current = {
      startX: rect.left,
      startY: rect.top,
      pointerX: event.clientX,
      pointerY: event.clientY,
    };
    hasMovedRef.current = false;

    try {
      if (typeof event.currentTarget.setPointerCapture === "function") {
        event.currentTarget.setPointerCapture(event.pointerId);
      }
    } catch (err) {
      // Ignore in mock/unsupported environments
    }
  };

  const handlePointerMove = (event) => {
    if (dragStartRef.current.pointerX === null || dragStartRef.current.pointerY === null) {
      return;
    }

    const deltaX = event.clientX - dragStartRef.current.pointerX;
    const deltaY = event.clientY - dragStartRef.current.pointerY;

    if (!hasMovedRef.current) {
      if (Math.hypot(deltaX, deltaY) < 4) {
        return;
      }
      hasMovedRef.current = true;
      setIsDragging(true);
    }

    const capsuleEl = capsuleRef.current;
    const width = capsuleEl ? capsuleEl.offsetWidth : 120;
    const height = capsuleEl ? capsuleEl.offsetHeight : 44;

    const margin = 8;
    const maxX = Math.max(margin, window.innerWidth - width - margin);
    const maxY = getCapsuleMaxY({
      viewportHeight: window.innerHeight,
      capsuleHeight: height,
      margin,
      isZenMode,
      viewportWidth: window.innerWidth,
    });

    const rawX = dragStartRef.current.startX + deltaX;
    const rawY = dragStartRef.current.startY + deltaY;

    const clampedX = Math.min(Math.max(margin, rawX), maxX);
    const clampedY = Math.min(Math.max(margin, rawY), maxY);

    setPosition({ x: clampedX, y: clampedY });
  };

  const handlePointerUp = (event) => {
    if (dragStartRef.current.pointerX === null && dragStartRef.current.pointerY === null) {
      return;
    }

    try {
      if (typeof event.currentTarget.releasePointerCapture === "function") {
        if (
          typeof event.currentTarget.hasPointerCapture !== "function" ||
          event.currentTarget.hasPointerCapture(event.pointerId)
        ) {
          event.currentTarget.releasePointerCapture(event.pointerId);
        }
      }
    } catch (err) {
      // Ignore in mock/unsupported environments
    }

    dragStartRef.current = { startX: 0, startY: 0, pointerX: null, pointerY: null };

    if (hasMovedRef.current) {
      // Ensure click capture ignores the subsequent click event
      setTimeout(() => {
        setIsDragging(false);
        hasMovedRef.current = false;
      }, 60);
    } else {
      setIsDragging(false);
    }
  };

  const handleClickCapture = (event) => {
    if (hasMovedRef.current || isDragging) {
      event.stopPropagation();
      event.preventDefault();
    }
  };

  // Sync scrubber value with current verse when popover is not open
  useEffect(() => {
    if (!anchorEl) {
      setScrubberVerse(currentVerse);
    }
  }, [currentVerse, anchorEl]);

  const isPopoverOpen = Boolean(anchorEl);

  const handleOpenDashboard = (event) => {
    if (hasMovedRef.current || isDragging) return;
    setScrubberVerse(currentVerse);
    setAnchorEl(capsuleRef.current || event.currentTarget);
  };

  const handleCloseDashboard = () => {
    setAnchorEl(null);
  };

  const handleScrubberChange = (event, value) => {
    setScrubberVerse(value);
  };

  const handleScrubberCommit = (event, value) => {
    setScrubberVerse(value);
    scrollToVerse(value);
  };

  const handleMarkCompleted = () => {
    toggleCompleted();
    if (!isCompleted) {
      setJustCompletedAnim(true);
      if (navigator.vibrate) navigator.vibrate([15, 30, 15]);
      setTimeout(() => setJustCompletedAnim(false), 2000);
    }
  };

  // Preview snippet of the verse being scrubbed
  const previewText = useMemo(() => {
    const text = verseTextMap[String(scrubberVerse)] || "";
    if (!text) return "";
    return text.length > 55 ? `${text.slice(0, 52)}...` : text;
  }, [scrubberVerse, verseTextMap]);

  const isInteractive = isPopoverOpen || isHovered || isDragging;
  const isHidden = shouldHideProgressCapsule({
    isScrolling,
    scrollDirection,
    isInteractive,
    hasSelection,
  });
  const capsuleOpacity = isHidden ? 0 : 1;

  const isMinimal = progressStyle === "minimal";

  const isUpperHalf = position
    ? position.y < (typeof window !== "undefined" ? window.innerHeight / 2 : 400)
    : false;
  const isLeftHalf = position
    ? position.x < (typeof window !== "undefined" ? window.innerWidth / 2 : 400)
    : false;

  if (progressStyle === "top_only" || progressStyle === "hidden") {
    return null;
  }

  return (
    <>
      <Box
        data-testid="progress-capsule-safe-area"
        aria-hidden
        sx={{
          display: { xs: hasSelection ? "none" : "block", sm: "none" },
          position: "fixed",
          left: 0,
          right: 0,
          bottom: getCapsuleDockBottom({ isZenMode }),
          height: 36,
          zIndex: 1240,
          pointerEvents: "none",
          bgcolor: "background.paper",
        }}
      />
      {/* Floating Capsule Bar (Draggable) */}
      <Box
        ref={capsuleRef}
        data-testid="reading-progress-capsule"
        data-capsule-hidden={isHidden ? "true" : "false"}
        aria-hidden={isHidden}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        onClickCapture={handleClickCapture}
        style={
          position
            ? {
                left: `${position.x}px`,
                top: `${position.y}px`,
                right: "auto",
                bottom: "auto",
              }
            : undefined
        }
        sx={{
          position: "fixed",
          ...(!position && {
            bottom: getCapsuleDockBottom({ isZenMode }),
            right: { xs: 16, sm: 26 },
          }),
          zIndex: 1250,
          opacity: capsuleOpacity,
          pointerEvents: isHidden ? "none" : "auto",
          touchAction: "none",
          userSelect: "none",
          WebkitUserSelect: "none",
          cursor: isDragging ? "grabbing" : "grab",
          transition: isDragging
            ? "none"
            : "opacity 220ms ease, transform 220ms ease, bottom 220ms ease, box-shadow 200ms ease",
          transform: isHidden
            ? "translateY(10px)"
            : !isDragging && isHovered && !hasSelection
              ? "translateY(-2px)"
              : "none",
        }}
      >
        {isMinimal ? (
          // Minimal Circle Ring Mode
          <Tooltip
            title={
              isDragging
                ? ""
                : `${labels.currentReading || "当前阅读"}: ${currentVerse}/${totalVerses} · ${scrollPercent}%`
            }
            placement={isLeftHalf ? "right" : "left"}
            arrow
            disableHoverListener={isDragging}
          >
            <Box
              onClick={handleOpenDashboard}
              sx={{
                width: 44,
                height: 44,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "inherit",
                bgcolor: (t) =>
                  t.palette.mode === "dark" ? "#181c20" : "#ffffff",
                border: "1px solid",
                borderColor: (t) =>
                  t.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.12)"
                    : "rgba(0, 0, 0, 0.08)",
                boxShadow: (t) =>
                  isDragging
                    ? t.palette.mode === "dark"
                      ? "0 8px 30px rgba(0,0,0,0.6)"
                      : "0 8px 30px rgba(0,0,0,0.25)"
                    : t.palette.mode === "dark"
                      ? "0 4px 18px rgba(0,0,0,0.4)"
                      : "0 4px 18px rgba(0,0,0,0.12)",
                "&:hover": {
                  boxShadow: (t) =>
                    t.palette.mode === "dark"
                      ? "0 6px 24px rgba(0,0,0,0.55)"
                      : "0 6px 24px rgba(0,0,0,0.18)",
                },
              }}
            >
              <ProgressRing
                percent={scrollPercent}
                size={32}
                stroke={3}
                isCompleted={isCompleted}
              />
            </Box>
          </Tooltip>
        ) : (
          // Full Smart Capsule Mode
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.2,
              height: { xs: 28, sm: 34 },
              px: { xs: 1, sm: 1.25 },
              py: 0.3,
              borderRadius: 999,
              cursor: "inherit",
              bgcolor: (t) =>
                t.palette.mode === "dark" ? "#161a1e" : "#ffffff",
              border: "1px solid",
              borderColor: (t) =>
                isCompleted
                  ? t.palette.mode === "dark"
                    ? "rgba(226, 196, 138, 0.35)"
                    : "rgba(139, 105, 20, 0.3)"
                  : t.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.12)"
                    : "rgba(0, 0, 0, 0.08)",
              boxShadow: (t) =>
                isDragging
                  ? t.palette.mode === "dark"
                    ? "0 8px 30px rgba(0,0,0,0.6)"
                    : "0 8px 30px rgba(0,0,0,0.25)"
                  : t.palette.mode === "dark"
                    ? "0 4px 20px rgba(0,0,0,0.38)"
                    : "0 4px 20px rgba(0,0,0,0.1)",
              transition: "border-color 200ms ease, box-shadow 200ms ease",
              "&:hover": {
                borderColor: "primary.main",
                boxShadow: (t) =>
                  t.palette.mode === "dark"
                    ? "0 6px 24px rgba(0,0,0,0.55)"
                    : "0 6px 24px rgba(0,0,0,0.16)",
              },
            }}
            onClick={handleOpenDashboard}
          >
            <ProgressRing
              percent={scrollPercent}
              size={26}
              stroke={2.8}
              isCompleted={isCompleted}
            />

            {/* Verse Ratio & Time */}
            <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.6 }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: "0.74rem", sm: "0.8rem" },
                  color: "text.primary",
                  lineHeight: 1,
                }}
              >
                {currentVerse}
                <Typography
                  component="span"
                  sx={{
                    fontWeight: 500,
                    fontSize: { xs: "0.68rem", sm: "0.72rem" },
                    color: "text.secondary",
                    ml: 0.25,
                  }}
                >
                  /{totalVerses}节
                </Typography>
              </Typography>

              {estimatedMinutesRemaining > 0 && (
                <Typography
                  variant="caption"
                  sx={{
                    color: "text.secondary",
                    fontSize: { xs: "0.66rem", sm: "0.7rem" },
                    display: "inline",
                    whiteSpace: "nowrap",
                  }}
                >
                  · 约{estimatedMinutesRemaining}分
                </Typography>
              )}
            </Box>

            {/* Mini toggle to collapse into circle mode */}
            <Tooltip
              title={labels.collapseProgress || "收起为极简微标"}
              placement="top"
              disableHoverListener={isDragging}
            >
              <IconButton
                size="small"
                onPointerDown={(e) => {
                  e.stopPropagation();
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  setStyle("minimal");
                }}
                sx={{
                  p: 0.25,
                  ml: 0.1,
                  display: { xs: "none", sm: "inline-flex" },
                  color: "text.secondary",
                  cursor: "pointer",
                  "&:hover": { color: "text.primary" },
                }}
              >
                <CloseFullscreenIcon sx={{ fontSize: 13 }} />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </Box>

      {/* Expandable Chapter Reading Dashboard (Popover) */}
      <Popover
        open={isPopoverOpen}
        anchorEl={anchorEl}
        onClose={handleCloseDashboard}
        anchorOrigin={{
          vertical: isUpperHalf ? "bottom" : "top",
          horizontal: isLeftHalf ? "left" : "right",
        }}
        transformOrigin={{
          vertical: isUpperHalf ? "top" : "bottom",
          horizontal: isLeftHalf ? "left" : "right",
        }}
        PaperProps={{
          sx: {
            width: { xs: 310, sm: 360 },
            maxHeight: "82vh",
            p: 2.2,
            borderRadius: 3.5,
            bgcolor: (t) =>
              t.palette.mode === "dark"
                ? "rgba(22, 26, 30, 0.94)"
                : "rgba(255, 255, 255, 0.96)",
            backdropFilter: "blur(20px)",
            border: "1px solid",
            borderColor: "divider",
            boxShadow: (t) =>
              t.palette.mode === "dark"
                ? "0 16px 40px rgba(0,0,0,0.6)"
                : "0 16px 40px rgba(0,0,0,0.15)",
            display: "flex",
            flexDirection: "column",
            gap: 2,
            overflowY: "auto",
          },
        }}
      >
        {/* 1. Dashboard Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 800,
                fontSize: "1.05rem",
                color: "text.primary",
                lineHeight: 1.3,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {chapterTitle}
            </Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.8,
                mt: 0.4,
                flexWrap: "wrap",
              }}
            >
              <Chip
                size="small"
                label={`第 ${currentVerse} 节 · ${scrollPercent}%`}
                sx={{
                  fontWeight: 600,
                  fontSize: "0.72rem",
                  height: 22,
                  bgcolor: (t) =>
                    t.palette.mode === "dark"
                      ? "rgba(255,255,255,0.08)"
                      : "rgba(0,0,0,0.06)",
                }}
              />
              {estimatedMinutesRemaining > 0 ? (
                <Typography
                  variant="caption"
                  sx={{
                    color: "text.secondary",
                    display: "flex",
                    alignItems: "center",
                    gap: 0.4,
                    fontSize: "0.72rem",
                  }}
                >
                  <AccessTimeIcon sx={{ fontSize: 13 }} />
                  约需 {estimatedMinutesRemaining} 分钟读完
                </Typography>
              ) : (
                <Typography
                  variant="caption"
                  sx={{
                    color: "success.main",
                    fontWeight: 600,
                    fontSize: "0.72rem",
                  }}
                >
                  已读至章末
                </Typography>
              )}
            </Box>
          </Box>

          {/* Mark as Completed Celebration Button */}
          <Tooltip
            title={isCompleted ? "点击取消已读标记" : "标记本章为已读"}
            placement="left"
          >
            <Button
              size="small"
              variant={isCompleted ? "contained" : "outlined"}
              color={isCompleted ? "primary" : "inherit"}
              onClick={handleMarkCompleted}
              startIcon={
                isCompleted ? (
                  <CheckCircleIcon sx={{ fontSize: 16 }} />
                ) : (
                  <CheckCircleOutlineIcon sx={{ fontSize: 16 }} />
                )
              }
              sx={{
                textTransform: "none",
                borderRadius: 999,
                fontSize: "0.72rem",
                py: 0.4,
                px: 1.2,
                flexShrink: 0,
                ...(justCompletedAnim && {
                  animation: "completedPulse 600ms ease",
                  "@keyframes completedPulse": {
                    "0%": { transform: "scale(1)" },
                    "50%": { transform: "scale(1.15)" },
                    "100%": { transform: "scale(1)" },
                  },
                }),
              }}
            >
              {isCompleted ? "已读完" : "打卡"}
            </Button>
          </Tooltip>
        </Box>

        {/* 2. Interactive Verse Scrubber Slider */}
        <Box
          sx={{
            p: 1.5,
            borderRadius: 2.5,
            bgcolor: (t) =>
              t.palette.mode === "dark"
                ? "rgba(255,255,255,0.04)"
                : "rgba(0,0,0,0.03)",
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 0.5,
            }}
          >
            <Typography
              variant="caption"
              sx={{ fontWeight: 700, color: "text.primary" }}
            >
              🚀 经文穿梭滑块
            </Typography>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                color: "primary.main",
                bgcolor: "action.hover",
                px: 0.8,
                py: 0.15,
                borderRadius: 1,
              }}
            >
              第 {scrubberVerse} / {totalVerses} 节
            </Typography>
          </Box>

          <Slider
            value={scrubberVerse}
            min={firstVerse}
            max={lastVerse}
            step={1}
            onChange={handleScrubberChange}
            onChangeCommitted={handleScrubberCommit}
            size="small"
            sx={{
              color: "primary.main",
              py: 1,
              "& .MuiSlider-thumb": {
                width: 16,
                height: 16,
                "&:hover, &.Mui-focusVisible": {
                  boxShadow: "0 0 0 8px rgba(25, 118, 210, 0.16)",
                },
              },
            }}
          />

          {/* Live Preview Box */}
          {previewText && (
            <Fade in={Boolean(previewText)}>
              <Box
                onClick={() => scrollToVerse(scrubberVerse)}
                sx={{
                  mt: 0.5,
                  p: 1,
                  borderRadius: 1.5,
                  bgcolor: (t) =>
                    t.palette.mode === "dark"
                      ? "rgba(0,0,0,0.3)"
                      : "rgba(255,255,255,0.7)",
                  border: "1px dashed",
                  borderColor: "divider",
                  cursor: "pointer",
                  "&:hover": { borderColor: "primary.main" },
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: "text.secondary",
                    display: "block",
                    lineHeight: 1.4,
                    fontSize: "0.72rem",
                  }}
                >
                  <strong style={{ color: theme.palette.primary.main }}>
                    [{scrubberVerse}节]
                  </strong>{" "}
                  {previewText}
                </Typography>
              </Box>
            </Fade>
          )}
        </Box>

        {/* 3. Section Outline Landmarks (小标题路标直达) */}
        {sectionList && sectionList.length > 0 && (
          <Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                mb: 1,
              }}
            >
              <FormatListBulletedIcon
                sx={{ fontSize: 14, color: "text.secondary" }}
              />
              <Typography
                variant="caption"
                sx={{ fontWeight: 700, color: "text.primary" }}
              >
                章节小标题路标 ({sectionList.length})
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 0.7,
                maxHeight: 130,
                overflowY: "auto",
                pr: 0.5,
              }}
            >
              {sectionList.map((section, idx) => {
                const isActive = currentSection?.verse === section.verse;
                return (
                  <Chip
                    key={`${section.verse}-${idx}`}
                    label={`${section.title} (${section.verse}节)`}
                    size="small"
                    color={isActive ? "primary" : "default"}
                    variant={isActive ? "filled" : "outlined"}
                    onClick={() => {
                      scrollToVerse(section.verse);
                    }}
                    sx={{
                      fontSize: "0.72rem",
                      fontWeight: isActive ? 700 : 500,
                      cursor: "pointer",
                      "&:hover": {
                        bgcolor: isActive ? "primary.dark" : "action.hover",
                      },
                    }}
                  />
                );
              })}
            </Box>
          </Box>
        )}

        {/* 4. Quick Actions Toolbar */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            pt: 1,
            borderTop: "1px solid",
            borderColor: "divider",
          }}
        >
          <Box sx={{ display: "flex", gap: 0.5 }}>
            <Tooltip title="回到开头" placement="top">
              <IconButton
                size="small"
                onClick={scrollToTop}
                sx={{ p: 0.6, border: "1px solid", borderColor: "divider" }}
              >
                <VerticalAlignTopIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="定位当前焦点节" placement="top">
              <IconButton
                size="small"
                onClick={() => scrollToVerse(currentVerse)}
                sx={{ p: 0.6, border: "1px solid", borderColor: "divider" }}
              >
                <CenterFocusStrongIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          </Box>

          {/* Display Mode Switch */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
            <Typography
              variant="caption"
              sx={{ fontSize: "0.7rem", color: "text.secondary" }}
            >
              显示:
            </Typography>
            <Button
              size="small"
              variant={isMinimal ? "contained" : "outlined"}
              onClick={() => setStyle(isMinimal ? "full" : "minimal")}
              sx={{
                textTransform: "none",
                fontSize: "0.7rem",
                borderRadius: 999,
                px: 1,
                py: 0.15,
                minWidth: 0,
              }}
            >
              {isMinimal ? "展开胶囊" : "极简微标"}
            </Button>
          </Box>
        </Box>
      </Popover>
    </>
  );
};

export default ChapterProgressCapsule;
